import { RootState } from '../../store';
import { createSelector } from '@reduxjs/toolkit';
import * as Rm from '../../data/room';
import { Vector2D } from '@digital-bridge/fine';

export const core = (state: RootState) => state.core;

export const clientId = createSelector(core, state => state.clientId);
export const locale = createSelector(core, state => state.locale);
export const currency = createSelector(core, state => state.currency);

const tenantMap: Record<string, string | undefined> = {
    'bbb8ac6d-83d3-4eb2-93f2-c5a6aed7aea4': 'test',
    '27af925f-fbab-4641-8b7a-8dcb7cbb8a95': 'moda',
    '3d2730da-52c3-4ee1-a2aa-e4aef71b3e75': 'bda-da-dk',
    '445747eb-b3a2-494e-bb87-743e41c130e8': 'bda-en-gb',
    '472d8a32-5e15-4948-9c97-d196b0e93d48': 'article',
    '9a0f6d7bf1db4cf9be24d9c1c0a46c77': 'victorianplumbing',
    '2e3d74d3-f67f-4cd3-8adc-9e94edf41ff3': 'visvictorianplumbing',
    '4595af91-5e49-4afa-8b6f-df2cf6fdfab4': 'easybathrooms',
};
export const tenantIdentifier = createSelector(clientId, clientId =>
    clientId ? tenantMap[clientId] : 'moda',
);

export const currentUrl = createSelector(core, state => state.currentUrl);
export const environment = createSelector(core, state => state.environment);
export const error = createSelector(core, state => state.error);
export const gtm = createSelector(core, state => state.gtm);
export const loading = createSelector(core, state => state.loading);
export const logLevel = createSelector(core, state => state.logLevel);
export const open = createSelector(core, state => state.open);
export const range = createSelector(core, state => state.range);

export const serviceUrls = createSelector(core, state => state.serviceUrls);
export const arCompatibility = createSelector(core, state => state.arCompatibility);
export const isAndroid = createSelector(core, state => state.device?.isAndroid);
export const isIOS = createSelector(core, state => state.device?.isIOS);
export const isMobile = createSelector(core, state => state.device?.isMobile);
export const isSafari = createSelector(core, state => state.device?.isSafari);
export const isFirefox = createSelector(core, state => state.device?.isFirefox);

export const view = createSelector(core, state => state.view);
export const viewData = createSelector(core, state => state.viewData);

/**  Select the viewData for Visual Space. undefined if not in space mode */
export const space = createSelector(viewData, state =>
    state?.mode === 'space' ? state : undefined,
);

/**
 * Select the viewData for Visual Inspiration. Undefined if we're not in inspiration mode
 */
export const inspirationData = createSelector(viewData, state =>
    state?.mode === 'inspiration' ? state : undefined,
);

/**
 * Select the viewData for Visual Configuration. Undefined if we're not in configuration mode
 */
export const configuration = createSelector(viewData, state =>
    state?.mode === 'configuration' ? state : undefined,
);

export const arModalText = createSelector(inspirationData, inspo => inspo?.launchData.modalText);
export const inspirationProductId = createSelector(
    inspirationData,
    inspo => inspo?.launchData.productId,
);

export const modelsUrl = createSelector(serviceUrls, state => state?.models);
export const productDataUrl = createSelector(serviceUrls, state => state?.productData);
export const shortLinkServiceUrl = createSelector(serviceUrls, state => state?.shortLink.service);
export const shortLinkFrontendUrl = createSelector(serviceUrls, state => state?.shortLink.frontend);
export const layoutUrl = createSelector(serviceUrls, state => state?.layout);
export const arUrl = createSelector(serviceUrls, state => state?.ar);
export const modelGeneratorUrl = createSelector(serviceUrls, state => state?.modelGenerator);
export const configurationServiceUrl = createSelector(serviceUrls, state => state?.configuration);
export const particleMappingServiceUrl = createSelector(
    serviceUrls,
    state => state?.particleMapping,
);

export const launchData = createSelector(space, state => state?.launchData);
export const configurationLaunchData = createSelector(configuration, state => state?.launchData);

export const room = createSelector(space, state =>
    state?.room ? Rm.deserialise(state.room) : undefined,
);

/**
 * Find the dimensions of the current room in a Vector(x, y). If the current room is non-existant,
 * returns a Vector(0, 0)
 */
export const roomDimensions = createSelector(room, state =>
    state ? Rm.dimensions(state) : new Vector2D([0, 0]),
);

export const featureFlags = createSelector(core, state => state.featureFlags);
